import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { MenuOption, ModuleMenuOptions } from '../models/menu-option.model';
import { SessionService } from '../auth/services/session.service';
// import { DESCRIPCION_BASE_PATH } from '../constants/routes'; // Ya no se usa

@Injectable({
  providedIn: 'root'
})
export class ModuleMenuService {
  private currentModuleMenuOptions = new BehaviorSubject<MenuOption[]>([]);
  private currentModuleId = new BehaviorSubject<string>('');
  private currentModuleName = new BehaviorSubject<string>('');
  private currentModuleIcon = new BehaviorSubject<string>('');
  
  // Caché de opciones de menú por módulo
  private menuOptionsCache: { [moduleId: string]: ModuleMenuOptions } = {};

  constructor(private http: HttpClient, private sessionService: SessionService) {}

  /**
   * Obtiene las opciones de menú para un módulo específico
   * @param moduleId Identificador del módulo
   * @returns Observable con las opciones de menú
   */
  getMenuOptionsForModule(moduleId: string): Observable<MenuOption[]> {
    const session = this.sessionService.getCurrentSession();
    let menuFileId = 'public'; // Por defecto, menú público

    if (session.isAuthenticated && session.user?.userType) {
      // Si el usuario está autenticado, usar su rol como ID del menú
      menuFileId = session.user.userType.toLowerCase();
    } else {
      // Para usuarios no autenticados, usar el ID del módulo si es específico (ej. 'home')
      // o mantener 'public' como genérico.
      if (['home'].includes(moduleId)) {
        menuFileId = moduleId;
      }
    }

    // Si ya tenemos las opciones en caché, las devolvemos
    if (this.menuOptionsCache[menuFileId]) {
      this.updateCurrentModuleData(this.menuOptionsCache[menuFileId]);
      return of(this.menuOptionsCache[menuFileId].options);
    }

    // Cargar el archivo JSON correspondiente al rol o sección pública
    return this.http.get<any>(`assets/data/menu/${menuFileId}.json`).pipe(
      map(response => {
        const moduleOptions: ModuleMenuOptions = {
          moduleId: response.moduleId || menuFileId,
          moduleName: response.moduleName || menuFileId,
          moduleIcon: response.moduleIcon || 'pi pi-folder',
          options: response.options || []
        };
        return moduleOptions;
      }),
      tap(moduleOptions => {
        this.menuOptionsCache[menuFileId] = moduleOptions;
        this.updateCurrentModuleData(moduleOptions);
      }),
      map(moduleOptions => moduleOptions.options),
      catchError(error => {
        console.error(`Error cargando opciones para el menú ${menuFileId}:`, error);
        return of([]);
      })
    );
  }

  /**
   * Actualiza los datos del módulo actual
   */
  private updateCurrentModuleData(moduleOptions: ModuleMenuOptions): void {
    this.currentModuleId.next(moduleOptions.moduleId);
    this.currentModuleName.next(moduleOptions.moduleName);
    this.currentModuleIcon.next(moduleOptions.moduleIcon);
    this.currentModuleMenuOptions.next(moduleOptions.options);
  }

  /**
   * Obtiene las opciones de menú actuales
   */
  get currentMenuOptions$(): Observable<MenuOption[]> {
    return this.currentModuleMenuOptions.asObservable();
  }

  /**
   * Obtiene el nombre del módulo actual
   */
  get currentModuleName$(): Observable<string> {
    return this.currentModuleName.asObservable();
  }

  /**
   * Obtiene el icono del módulo actual
   */
  get currentModuleIcon$(): Observable<string> {
    return this.currentModuleIcon.asObservable();
  }

  /**
   * Obtiene el ID del módulo actual
   */
  get currentModuleId$(): Observable<string> {
    return this.currentModuleId.asObservable();
  }
}
